/**
 * @file Album component. Display an album within an album folder.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import CustomControl from '../CustomControl';
import { getSelectedAlbumData } from '../../../util/discover-album-folder';
import AlbumsIcon from '../../icons/AlbumsIcon';
import Badge from '../Badge';
import { ALBUM_SUBTYPE } from '../../../constants/albums';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    join_code: PropTypes.string,
    is_free: PropTypes.bool,
    checkout_description: PropTypes.string,
    subscription_album_folder: PropTypes.shape({
      id: PropTypes.string,
      price: PropTypes.shape({
        value: PropTypes.number,
      }),
    }),
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  onSelectAlbum: PropTypes.func.isRequired,
  onDeselectAlbum: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isJoined: PropTypes.bool,
  hasMatchLink: PropTypes.bool,
  disabled: PropTypes.bool,
};

const AlbumItem = ({
  album,
  albumFolder,
  onSelectAlbum,
  onDeselectAlbum,
  isSelected,
  isJoined,
  hasMatchLink,
  disabled,
}) => {
  const toggleAlbum = (identity, event) => {
    event.preventDefault();

    if (isJoined || disabled) {
      return;
    }

    const albumData = getSelectedAlbumData(album, albumFolder);

    if (!isSelected) {
      onSelectAlbum(albumData);
    } else {
      onDeselectAlbum(albumData);
    }
  };

  const shouldRenderFreeBadge = () =>
    album.is_free && album.album_subtype !== ALBUM_SUBTYPE.EVENTS;

  // On PPD, direct the user to the gallery level. This prevents the
  // issue where the user is unable to purchase print packages since
  // said items are tied to the gallery, not the albums.
  const matchLinkEntityId = albumFolder?.root?.picture_day_enabled
    ? albumFolder.root.id
    : album.id;

  return (
    <Link
      to={
        hasMatchLink
          ? `/app/shutterbug/hierarchy-items/${matchLinkEntityId}/matches`
          : ''
      }
      className="discover-album-folder-container"
      onClick={!hasMatchLink ? event => toggleAlbum(album, event) : undefined}
    >
      <AlbumsIcon color="#0d84fe" size={18} className="mr-2 flex-none" />

      <span className="text-dark mr-1">
        <span className="d-flex align-items-center">
          {album.name}

          {shouldRenderFreeBadge() && (
            <Badge
              badgeSize="sm"
              className="mx-1 text-bold"
              badgeStyle="success-vibrant"
            >
              FREE!
            </Badge>
          )}
        </span>

        {album.checkout_description && (
          <span className="d-block text-faded text-sm">
            {album.checkout_description}
          </span>
        )}
      </span>

      <div className="ml-auto">
        {isJoined && !hasMatchLink && (
          <span className="d-block text-xs text-right text-success-vibrant">
            <span className="d-block">Already Joined</span>
            <span className="d-block text-faded">(No matches yet)</span>
          </span>
        )}

        {!isJoined && !hasMatchLink && (
          <CustomControl
            id={album.id}
            type="checkbox"
            size="xl"
            checked={isSelected}
            readOnly
            disabled={disabled}
            className="ml-auto"
          />
        )}

        {hasMatchLink && (
          <span className="text-xs text-primary text-right">
            View matches &rarr;
          </span>
        )}
      </div>
    </Link>
  );
};

AlbumItem.propTypes = propTypes;

export default AlbumItem;
